import { filterTradesByTrailingDays, getTradePnl, getTradeTimestampMs, normalizeTradeStatus } from './authoritativeTradeData';
import { readPersistedTradePositionExcursionValue } from './tradeExcursions';
import { getDashboardTimeRangeDays } from '../dashboardTimeRange';
import { filterCanonicalRealizedTrades } from './dashboardTradeSource';

export type DashboardTimeRange = 'week' | 'month' | 'year';

export type BucketSpec = {
  label: string;
  min?: number;
  max?: number;
  includeMin?: boolean;
  includeMax?: boolean;
};

export type BucketItem = {
  name: string;
  pnl: number;
  pct: number;
  trades: number;
};

export type BucketAnalyticsState = 'data' | 'empty' | 'na';

export type BucketAnalyticsResult = {
  items: BucketItem[];
  maxPnl: number;
  state: BucketAnalyticsState;
  totalTrades: number;
  eligibleTrades: number;
};

type MetricResolver = (trade: Record<string, any>) => number | null;

const ENTRY_PRICE_FIELDS = [
  'entryPrice',
  'avgEntryPrice',
  'avgEntry',
  'entry_price',
  'price',
  'avgPrice',
  'avg_price',
] as const;

const EXIT_PRICE_FIELDS = [
  'exitPrice',
  'avgExitPrice',
  'avgExit',
  'exit_price',
] as const;

const NOTIONAL_FIELDS = [
  'notionalVolume',
  'dollarVolume',
  'tradeNotional',
  'notional',
  'entryNotional',
  'filledNotional',
  'entryValue',
  'tradeValue',
] as const;

const QUANTITY_FIELDS = [
  'volume',
  'qty',
  'quantity',
  'size',
  'shares',
] as const;

const RVOL_FIELDS = [
  'relativeVolatility',
  'relativeVol',
  'relVolatility',
  'relVol',
  'rvol',
  'RVOL',
  'priorDayRvol',
  'prior_day_rvol',
] as const;

const MOVEMENT_FIELDS = [
  'movementPercent',
  'movementPct',
  'priceMovementPercent',
  'priceMovementPct',
  'priceMovePercent',
  'priceMovePct',
  'movePercent',
  'movePct',
  'priceChangePercent',
  'priceChangePct',
] as const;

const ENTRY_ATR_FIELDS = [
  'entryPctAtr',
  'entryPercentAtr',
  'entryPercentageOfAtr',
  'entryAtrPct',
  'entryPctOfAtr',
  'percentOfAtr',
] as const;

const ATR_FIELDS = [
  'symbolAtr',
  'atr',
  'ATR',
  'atrValue',
  'atr14',
  'atr_14',
  'avgTrueRange',
  'averageTrueRange',
] as const;

const OPENING_GAP_FIELDS = [
  'openingGapPercent',
  'openingGapPct',
  'openGapPercent',
  'openGapPct',
  'gapPercent',
  'gapPct',
  'openingGap',
  'openGap',
] as const;

const IN_TRADE_RANGE_FIELDS = [
  'inTradePriceRange',
  'intratradePriceRange',
  'inTradeRange',
  'intratradeRange',
  'tradeRange',
  'priceRangeDuringTrade',
] as const;

export const PRICE_BUCKETS: BucketSpec[] = [
  { label: '< $2.00', max: 2 },
  { label: '$2 - $4.99', min: 2, max: 5, includeMin: true },
  { label: '$5 - $9.99', min: 5, max: 10, includeMin: true },
  { label: '$10 - $19.99', min: 10, max: 20, includeMin: true },
  { label: '$20 - $49.99', min: 20, max: 50, includeMin: true },
  { label: '$50 - $99.99', min: 50, max: 100, includeMin: true },
  { label: '$100 - $199.99', min: 100, max: 200, includeMin: true },
  { label: '$200 - $499.99', min: 200, max: 500, includeMin: true },
  { label: '$500+', min: 500, includeMin: true },
];

export const NOTIONAL_VOLUME_BUCKETS: BucketSpec[] = [
  { label: '<1M', max: 1_000_000 },
  { label: '1M - 5M', min: 1_000_000, max: 5_000_000, includeMin: true },
  { label: '5M - 10M', min: 5_000_000, max: 10_000_000, includeMin: true },
  { label: '10M - 20M', min: 10_000_000, max: 20_000_000, includeMin: true },
  { label: '20M - 50M', min: 20_000_000, max: 50_000_000, includeMin: true },
  { label: '50M+', min: 50_000_000, includeMin: true },
];

export const RELATIVE_VOLATILITY_BUCKETS: BucketSpec[] = [
  { label: '0 - 24%', min: 0, max: 25, includeMin: true },
  { label: '25% - 49%', min: 25, max: 50, includeMin: true },
  { label: '50% - 74%', min: 50, max: 75, includeMin: true },
  { label: '75% - 99%', min: 75, max: 100, includeMin: true },
  { label: '100% - 124%', min: 100, max: 125, includeMin: true },
  { label: '125% - 149%', min: 125, max: 150, includeMin: true },
  { label: '150% - 199%', min: 150, max: 200, includeMin: true },
  { label: '200%+', min: 200, includeMin: true },
];

export const MOVEMENT_BUCKETS: BucketSpec[] = [
  { label: 'less than -10%', max: -10 },
  { label: '-2% to -10%', min: -10, max: -2, includeMin: true },
  { label: '-1% to -2%', min: -2, max: -1, includeMin: true },
  { label: '0 to -1%', min: -1, max: 0, includeMin: true },
  { label: '0 to +1%', min: 0, max: 1, includeMin: true },
  { label: '+1% to +2%', min: 1, max: 2, includeMin: true },
  { label: '+2% to +10%', min: 2, max: 10, includeMin: true },
  { label: '> +10%', min: 10, includeMin: true },
];

export const ENTRY_ATR_BUCKETS: BucketSpec[] = [
  { label: '-200% <', max: -200 },
  { label: '-199% to -100%', min: -200, max: -100, includeMin: true },
  { label: '-99% to -50%', min: -100, max: -50, includeMin: true },
  { label: '-49% to -25%', min: -50, max: -25, includeMin: true },
  { label: '-24% to +24%', min: -25, max: 25, includeMin: true },
  { label: '+25% to +49%', min: 25, max: 50, includeMin: true },
  { label: '+50% to +99%', min: 50, max: 100, includeMin: true },
  { label: '+100% to +199%', min: 100, max: 200, includeMin: true },
  { label: '200% >', min: 200, includeMin: true },
];

export const SYMBOL_ATR_BUCKETS: BucketSpec[] = [
  { label: '$0.00 - $0.09', min: 0, max: 0.1, includeMin: true },
  { label: '$0.10 - $0.24', min: 0.1, max: 0.25, includeMin: true },
  { label: '$0.25 - $0.49', min: 0.25, max: 0.5, includeMin: true },
  { label: '$0.50 - $0.99', min: 0.5, max: 1, includeMin: true },
  { label: '$1 - $1.99', min: 1, max: 2, includeMin: true },
  { label: '$2 - $4.99', min: 2, max: 5, includeMin: true },
  { label: '$5+', min: 5, includeMin: true },
];

export const OPENING_GAP_BUCKETS: BucketSpec[] = [
  { label: 'less than -2%', max: -2 },
  { label: '-1% to -2%', min: -2, max: -1, includeMin: true },
  { label: '0 to -1%', min: -1, max: 0, includeMin: true },
  { label: '0 to +1%', min: 0, max: 1, includeMin: true },
  { label: '+1% to +2%', min: 1, max: 2, includeMin: true },
  { label: '> +2%', min: 2, includeMin: true },
];

export const TRADED_VOLUME_BUCKETS: BucketSpec[] = [
  { label: '0 - 999', min: 0, max: 1_000, includeMin: true },
  { label: '1k - 4.9k', min: 1_000, max: 5_000, includeMin: true },
  { label: '5k - 9.9k', min: 5_000, max: 10_000, includeMin: true },
  { label: '10k - 19.9k', min: 10_000, max: 20_000, includeMin: true },
  { label: '20k - 49.9k', min: 20_000, max: 50_000, includeMin: true },
  { label: '50k - 99.9k', min: 50_000, max: 100_000, includeMin: true },
  { label: '100k+', min: 100_000, includeMin: true },
];

export const DURATION_BUCKETS: BucketSpec[] = [
  { label: '< 1m', max: 1 },
  { label: '1m - 1:59', min: 1, max: 2, includeMin: true },
  { label: '2m - 4:59', min: 2, max: 5, includeMin: true },
  { label: '5m - 9:59', min: 5, max: 10, includeMin: true },
  { label: '10m - 19:59', min: 10, max: 20, includeMin: true },
  { label: '20m - 39:59', min: 20, max: 40, includeMin: true },
  { label: '40m - 59:59', min: 40, max: 60, includeMin: true },
  { label: '1h+', min: 60, includeMin: true },
];

export const IN_TRADE_RANGE_BUCKETS: BucketSpec[] = [
  { label: '$0.00 - $0.09', min: 0, max: 0.1, includeMin: true },
  { label: '$0.10 - $0.24', min: 0.1, max: 0.25, includeMin: true },
  { label: '$0.25 - $0.49', min: 0.25, max: 0.5, includeMin: true },
  { label: '$0.50 - $0.99', min: 0.5, max: 1, includeMin: true },
  { label: '$1 - $4.99', min: 1, max: 5, includeMin: true },
  { label: '$5 - $9.99', min: 5, max: 10, includeMin: true },
  { label: '$10 - $24.99', min: 10, max: 25, includeMin: true },
  { label: '$25 >', min: 25, includeMin: true },
];

function toFiniteNumber(value: unknown): number | null {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function getMetricRows(trade: Record<string, any>): Record<string, any>[] {
  const rows: Record<string, any>[] = [trade];
  if (Array.isArray(trade?.executionsList)) rows.push(...trade.executionsList);
  if (Array.isArray(trade?.executions)) rows.push(...trade.executions);
  return rows;
}

function readNumericField(trade: Record<string, any>, fields: readonly string[]): number | null {
  for (const row of getMetricRows(trade)) {
    for (const field of fields) {
      const numeric = toFiniteNumber(row?.[field]);
      if (numeric != null) return numeric;
    }
  }
  return null;
}

function normalizeRatioToPercent(value: number | null): number | null {
  if (value == null) return null;
  return Math.abs(value) <= 10 ? value * 100 : value;
}

function buildZeroItems(buckets: BucketSpec[]): BucketItem[] {
  return buckets.map((bucket) => ({ name: bucket.label, pnl: 0, pct: 0, trades: 0 }));
}

function findBucket(value: number, buckets: BucketSpec[]): BucketSpec | null {
  for (const bucket of buckets) {
    const lowerOk = bucket.min == null
      ? true
      : (bucket.includeMin ? value >= bucket.min : value > bucket.min);
    const upperOk = bucket.max == null
      ? true
      : (bucket.includeMax ? value <= bucket.max : value < bucket.max);
    if (lowerOk && upperOk) return bucket;
  }
  return null;
}

export function filterTradesForDashboardRange(trades: Record<string, any>[], timeRange: DashboardTimeRange) {
  if (!Array.isArray(trades) || trades.length === 0) return [];
  return filterTradesByTrailingDays(filterCanonicalRealizedTrades(trades) as any[], getDashboardTimeRangeDays(timeRange));
}

export function buildBucketPerformance(params: {
  trades?: Record<string, any>[];
  timeRange: DashboardTimeRange;
  buckets: BucketSpec[];
  metric: MetricResolver;
}): BucketAnalyticsResult {
  const filteredTrades = filterTradesForDashboardRange(params.trades ?? [], params.timeRange);

  if (!filteredTrades.length) {
    return {
      items: buildZeroItems(params.buckets),
      maxPnl: 1,
      state: 'empty',
      totalTrades: 0,
      eligibleTrades: 0,
    };
  }

  const bucketTotals = new Map<string, BucketItem>(
    params.buckets.map((bucket) => [bucket.label, { name: bucket.label, pnl: 0, pct: 0, trades: 0 }])
  );

  let eligibleTrades = 0;

  filteredTrades.forEach((trade) => {
    const metricValue = params.metric(trade);
    if (metricValue == null || !Number.isFinite(metricValue)) return;
    const bucket = findBucket(metricValue, params.buckets);
    if (!bucket) return;
    const current = bucketTotals.get(bucket.label);
    if (!current) return;
    current.pnl += getTradePnl(trade);
    current.trades += 1;
    eligibleTrades += 1;
  });

  if (eligibleTrades === 0) {
    return {
      items: buildZeroItems(params.buckets),
      maxPnl: 1,
      state: 'na',
      totalTrades: filteredTrades.length,
      eligibleTrades: 0,
    };
  }

  const items = params.buckets.map((bucket) => bucketTotals.get(bucket.label) ?? { name: bucket.label, pnl: 0, pct: 0, trades: 0 });
  const totalPnlVolume = items.reduce((sum, item) => sum + Math.abs(item.pnl), 0);
  const maxPnl = Math.max(1, ...items.map((item) => Math.abs(item.pnl)));

  return {
    items: items.map((item) => ({
      ...item,
      pct: totalPnlVolume > 0 ? (Math.abs(item.pnl) / totalPnlVolume) * 100 : 0,
    })),
    maxPnl,
    state: 'data',
    totalTrades: filteredTrades.length,
    eligibleTrades,
  };
}

export function getTradeEntryPrice(trade: Record<string, any>): number | null {
  return readNumericField(trade, ENTRY_PRICE_FIELDS);
}

export function getTradeExitPrice(trade: Record<string, any>): number | null {
  return readNumericField(trade, EXIT_PRICE_FIELDS);
}

export function getTradeTradedQuantity(trade: Record<string, any>): number | null {
  const quantity = readNumericField(trade, QUANTITY_FIELDS);
  return quantity == null ? null : Math.abs(quantity);
}

export function getTradeNotionalVolume(trade: Record<string, any>): number | null {
  const explicit = readNumericField(trade, NOTIONAL_FIELDS);
  if (explicit != null) return Math.abs(explicit);

  const quantity = getTradeTradedQuantity(trade);
  const entryPrice = getTradeEntryPrice(trade);
  if (quantity == null || entryPrice == null) return null;
  return Math.abs(quantity * entryPrice);
}

export function getTradeDurationMinutes(trade: Record<string, any>): number | null {
  if (normalizeTradeStatus(trade as any) === 'open') return null;

  const explicit = readNumericField(trade, ['durationMinutes', 'durationMins', 'duration_min', 'minutesHeld']);
  if (explicit != null) return Math.max(0, explicit);

  const duration = readNumericField(trade, ['duration']);
  if (duration != null) return Math.max(0, duration);

  const entryMs = getTradeTimestampMs(trade as any);
  const exitMs = readNumericField(trade, [
    'closeTimestampMs',
    'exitTimestampMs',
    'exitTimestamp',
    'exitTime',
    'closeTime',
    'closedAt',
    'closed_at_utc',
  ]);

  if (entryMs == null || exitMs == null || exitMs < entryMs) return null;
  return Math.max(0, (exitMs - entryMs) / 60000);
}

export function getTradeRelativeVolatility(trade: Record<string, any>): number | null {
  const value = readNumericField(trade, RVOL_FIELDS);
  if (value == null) return null;
  return Math.abs(normalizeRatioToPercent(value) ?? 0);
}

export function getTradeMovementPct(trade: Record<string, any>): number | null {
  const explicit = readNumericField(trade, MOVEMENT_FIELDS);
  if (explicit != null) return explicit;

  const entryPrice = getTradeEntryPrice(trade);
  const exitPrice = getTradeExitPrice(trade);
  if (entryPrice == null || exitPrice == null || entryPrice === 0) return null;
  return ((exitPrice - entryPrice) / entryPrice) * 100;
}

export function getTradeEntryPctAtr(trade: Record<string, any>): number | null {
  const value = readNumericField(trade, ENTRY_ATR_FIELDS);
  return normalizeRatioToPercent(value);
}

export function getTradeSymbolAtr(trade: Record<string, any>): number | null {
  const value = readNumericField(trade, ATR_FIELDS);
  if (value == null) return null;
  return Math.abs(value);
}

export function getTradeOpeningGapPct(trade: Record<string, any>): number | null {
  return readNumericField(trade, OPENING_GAP_FIELDS);
}

export function getTradeInTradePriceRange(trade: Record<string, any>): number | null {
  const explicit = readNumericField(trade, IN_TRADE_RANGE_FIELDS);
  if (explicit != null) return Math.abs(explicit);

  const mfe = readPersistedTradePositionExcursionValue(trade, 'MFE')
    ?? readNumericField(trade, ['mfe', 'MFE', 'maxFavorableExcursion']);
  const mae = readPersistedTradePositionExcursionValue(trade, 'MAE')
    ?? readNumericField(trade, ['mae', 'MAE', 'maxAdverseExcursion']);

  if (mfe == null || mae == null) return null;
  return Math.max(0, mfe) + Math.max(0, Math.abs(Math.min(0, mae)));
}
